import { useState } from 'react';
import { Globe, User, X, Building2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useClients } from '@/hooks/useClients';
import { cn } from '@/lib/utils';

interface ScopeSelectorProps {
  scope: 'client' | 'global';
  clientName: string | null;
  onScopeChange: (client: { id: string; name: string } | null) => void;
}

/**
 * ScopeSelector — lets users switch between org-wide and client-scoped chat.
 *
 * Renders a clickable badge showing the current scope. Clicking the badge opens
 * a popover with a searchable client list. An X button clears back to org-wide
 * when a client scope is active.
 *
 * @param scope - Current scope: 'global' for org-wide, 'client' for client-scoped
 * @param clientName - Display name of the selected client (null when global)
 * @param onScopeChange - Called with client `{ id, name }` when selecting a client, or null for org-wide
 */
export function ScopeSelector({ scope, clientName, onScopeChange }: ScopeSelectorProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { clients, isLoading } = useClients();

  const activeClients = clients.filter((c) => !c.is_archived);
  const archivedClients = clients.filter((c) => c.is_archived);
  const searchLower = search.toLowerCase();
  const filteredActive = activeClients.filter((c) =>
    c.name.toLowerCase().includes(searchLower),
  );
  const filteredArchived = archivedClients.filter((c) =>
    c.name.toLowerCase().includes(searchLower),
  );

  const badgeClassName = cn(
    'inline-flex items-center gap-1 rounded-md border border-transparent',
    'px-2.5 py-0.5 text-xs font-medium bg-secondary text-muted-foreground',
    'hover:bg-secondary/80 cursor-pointer',
  );

  const handleClientSelect = (clientId: string, name: string) => {
    onScopeChange({ id: clientId, name });
    setOpen(false);
    setSearch('');
  };

  const handleOrgWideSelect = () => {
    onScopeChange(null);
    setOpen(false);
    setSearch('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onScopeChange(null);
  };

  return (
    <div className="flex items-center gap-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={badgeClassName}
            aria-label="Select scope"
          >
            {scope === 'client' && clientName ? (
              <>
                <User className="h-3 w-3 shrink-0" />
                <span>{clientName}</span>
              </>
            ) : (
              <>
                <Globe className="h-3 w-3 shrink-0" />
                <span>Organization-wide</span>
              </>
            )}
          </button>
        </PopoverTrigger>

        <PopoverContent className="w-64 p-0" align="start">
          {/* Search input */}
          <div className="p-2 border-b">
            <Input
              placeholder="Search clients..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 text-sm"
              autoFocus
            />
          </div>

          <ScrollArea className="max-h-64">
            <div className="p-1">
              {/* Org-wide option */}
              <button
                type="button"
                className="w-full flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground text-left"
                onClick={handleOrgWideSelect}
              >
                <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span>Organization-wide</span>
              </button>

              {/* Client list */}
              {isLoading ? (
                <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                  Loading clients...
                </div>
              ) : filteredActive.length === 0 && filteredArchived.length === 0 && search ? (
                <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                  No clients found
                </div>
              ) : (
                <>
                  {/* Active clients */}
                  {filteredActive.length > 0 && (
                    <>
                      <div className="flex items-center gap-2 px-2 py-1 mt-1">
                        <div className="h-px flex-1 bg-border" />
                        <span className="text-xs text-muted-foreground">Active</span>
                        <div className="h-px flex-1 bg-border" />
                      </div>
                      {filteredActive.map((client) => (
                        <button
                          key={client.id}
                          type="button"
                          className="w-full flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground text-left"
                          onClick={() => handleClientSelect(client.id, client.name)}
                        >
                          <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <span className="truncate">{client.name}</span>
                        </button>
                      ))}
                    </>
                  )}

                  {/* Archived clients */}
                  {filteredArchived.length > 0 && (
                    <>
                      <div className="flex items-center gap-2 px-2 py-1 mt-1">
                        <div className="h-px flex-1 bg-border" />
                        <span className="text-xs text-muted-foreground">Archived</span>
                        <div className="h-px flex-1 bg-border" />
                      </div>
                      {filteredArchived.map((client) => (
                        <button
                          key={client.id}
                          type="button"
                          className="w-full flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground text-left opacity-70"
                          onClick={() => handleClientSelect(client.id, client.name)}
                        >
                          <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <span className="truncate">{client.name}</span>
                          <span className="text-[10px] text-muted-foreground ml-auto shrink-0">Archived</span>
                        </button>
                      ))}
                    </>
                  )}
                </>
              )}
            </div>
          </ScrollArea>
        </PopoverContent>
      </Popover>

      {/* Clear button — only shown when client scope is active */}
      {scope === 'client' && clientName && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-5 w-5 rounded-full text-muted-foreground hover:text-foreground"
          onClick={handleClear}
          aria-label="Clear scope"
        >
          <X className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}
